<script>
  import { createEventDispatcher } from "svelte";
  import { uiFeatureStore } from "../../stores/uiFeatureStore.js";
  import { Button } from "paxray-components-svelte";
  import { KEYBOARD_ENTER_DARK_ICON } from '@utils/icons.js';

  export let open = false;
  /** Bind open to control visibility. Steps: { target, title, text, placement?, padding?, allowInteraction?, highlightSelector? }. */
  export let steps = [];
  export let currentStep = 0;
  export let target = "";
  export let title = "";
  export let text = "";
  export let placement = "bottom";
  export let padding = null;
  export let allowInteraction = true;
  export let labels = {
    previous: "Back",
    next: "Next",
    finish: "Done",
    skip: "Skip Tour",
    step: "Step",
    of: "of",
    unavailable: "This element is currently unavailable.",
  };

  const dispatch = createEventDispatcher();
  $: if (!$uiFeatureStore.platformFeatures.guidedTours) open = false;
  let rect = null;
  let highlightRects = [];
  let cardX = 0;
  let cardY = 0;
  let ready = false;
  let missing = false;
  let arrowPath = "";
  let viewportWidth = 0;
  let viewportHeight = 0;
  let enterHeld = false;
  $: if (!open) enterHeld = false;

  $: count = steps.length || 1;
  $: index = Math.max(0, Math.min(currentStep, count - 1));
  $: step = steps.length
    ? steps[index]
    : { target, title, text, placement, padding, allowInteraction };
  $: interactive = step.allowInteraction ?? allowInteraction;
  $: backdropPath =
    `M0 0H${viewportWidth}V${viewportHeight}H0Z` +
    highlightRects.map(roundedCutout).join("");

  function roundedCutout({ x, y, width, height, radius }) {
    const r = Math.min(radius, width / 2, height / 2);
    return (
      `M${x + r} ${y}H${x + width - r}Q${x + width} ${y} ${x + width} ${y + r}` +
      `V${y + height - r}Q${x + width} ${y + height} ${x + width - r} ${y + height}` +
      `H${x + r}Q${x} ${y + height} ${x} ${y + height - r}V${y + r}Q${x} ${y} ${x + r} ${y}Z`
    );
  }

  function close(reason) {
    open = false;
    dispatch(reason, { index, step });
    dispatch("close", { reason, index, step });
  }

  function navigate(direction) {
    currentStep = index + direction;
    dispatch(direction > 0 ? "next" : "previous", {
      index: currentStep,
      step: steps[currentStep],
    });
  }

  // Rendering at body level avoids clipping and stacking contexts of the caller.
  function mountGuide(node) {
    document.body.appendChild(node);
    const card = node.querySelector(".guide-card");
    const previousFocus = document.activeElement;
    let element;
    let animationFrame;
    let previousGeometry = "";

    function updateViewport() {
      viewportWidth = document.documentElement.clientWidth;
      viewportHeight = window.innerHeight;
    }

    function updatePosition() {
      updateViewport();
      if (!element) return;
      const targets = step.highlightSelector
        ? [...element.querySelectorAll(step.highlightSelector)]
        : [element];
      const boundsList = targets
        .filter(
          (target) =>
            target.isConnected &&
            getComputedStyle(target).visibility === "visible",
        )
        .map((target) => target.getBoundingClientRect())
        .filter((bounds) => bounds.width > 0 && bounds.height > 0);
      const bounds = boundsList.length
        ? {
            left: Math.min(...boundsList.map((item) => item.left)),
            top: Math.min(...boundsList.map((item) => item.top)),
            right: Math.max(...boundsList.map((item) => item.right)),
            bottom: Math.max(...boundsList.map((item) => item.bottom)),
          }
        : element.getBoundingClientRect();
      bounds.width = bounds.right - bounds.left;
      bounds.height = bounds.bottom - bounds.top;
      const visible = element.isConnected && boundsList.length > 0;
      const width = card.offsetWidth;
      const height = card.offsetHeight;
      const geometry = [
        bounds.left,
        bounds.top,
        bounds.width,
        bounds.height,
        width,
        height,
        viewportWidth,
        viewportHeight,
        visible,
        ...boundsList.flatMap((item) => [
          item.left,
          item.top,
          item.width,
          item.height,
        ]),
      ].join(",");
      if (geometry === previousGeometry) return;
      previousGeometry = geometry;
      missing = !visible;
      if (!visible) {
        rect = null;
        highlightRects = [];
        ready = false;
        return;
      }
      const gap = Math.max(
        0,
        step.padding ??
          padding ??
          parseFloat(getComputedStyle(node).paddingTop),
      );
      const left = Math.max(0, bounds.left - gap);
      const top = Math.max(0, bounds.top - gap);
      const right = Math.min(viewportWidth, bounds.right + gap);
      const bottom = Math.min(viewportHeight, bounds.bottom + gap);
      rect =
        right > left && bottom > top
          ? {
              x: left,
              y: top,
              width: right - left,
              height: bottom - top,
              radius: parseFloat(getComputedStyle(node).borderTopLeftRadius),
            }
          : null;
      highlightRects = boundsList
        .map((item) => {
          const x = Math.max(0, item.left - gap);
          const y = Math.max(0, item.top - gap);
          return {
            x,
            y,
            width: Math.min(viewportWidth, item.right + gap) - x,
            height: Math.min(viewportHeight, item.bottom + gap) - y,
            radius: parseFloat(getComputedStyle(node).borderTopLeftRadius),
          };
        })
        .filter((item) => item.width > 0 && item.height > 0);
      const margin = 16;
      const distance = gap + 56;
      const positions = {
        bottom: {
          x: bounds.left + (bounds.width - width) / 2,
          y: bounds.bottom + distance,
        },
        top: {
          x: bounds.left + (bounds.width - width) / 2,
          y: bounds.top - distance - height,
        },
        right: {
          x: bounds.right + distance,
          y: bounds.top + (bounds.height - height) / 2,
        },
        left: {
          x: bounds.left - distance - width,
          y: bounds.top + (bounds.height - height) / 2,
        },
      };
      const opposite = {
        bottom: "top",
        top: "bottom",
        left: "right",
        right: "left",
      };
      const requested = step.placement ?? placement;
      const preferred = Object.prototype.hasOwnProperty.call(
        positions,
        requested,
      )
        ? requested
        : "bottom";
      const overflow = ({ x, y }) =>
        Math.max(margin - x, 0) +
        Math.max(margin - y, 0) +
        Math.max(x + width + margin - viewportWidth, 0) +
        Math.max(y + height + margin - viewportHeight, 0);
      const candidates = [
        preferred,
        opposite[preferred],
        ...Object.keys(positions),
      ];
      const side = candidates.reduce(
        (best, candidate) =>
          overflow(positions[candidate]) < overflow(positions[best])
            ? candidate
            : best,
        preferred,
      );
      cardX = Math.max(
        margin,
        Math.min(positions[side].x, viewportWidth - width - margin),
      );
      cardY = Math.max(
        margin,
        Math.min(positions[side].y, viewportHeight - height - margin),
      );
      ready = true;
      const horizontal = side === "left" || side === "right";
      const startX = horizontal
        ? cardX + (side === "left" ? card.offsetWidth : 0)
        : Math.max(
            cardX + 24,
            Math.min(
              bounds.left + bounds.width / 2,
              cardX + card.offsetWidth - 24,
            ),
          );
      const startY = horizontal
        ? Math.max(
            cardY + 24,
            Math.min(
              bounds.top + bounds.height / 2,
              cardY + card.offsetHeight - 24,
            ),
          )
        : cardY + (side === "top" ? card.offsetHeight : 0);
      const endX = horizontal
        ? side === "left"
          ? bounds.left - gap
          : bounds.right + gap
        : bounds.left + bounds.width / 2;
      const endY = horizontal
        ? bounds.top + bounds.height / 2
        : side === "top"
          ? bounds.top - gap
          : bounds.bottom + gap;
      const length = Math.hypot(endX - startX, endY - startY);
      if (length < 24) {
        arrowPath = "";
        return;
      }
      const dx = (endX - startX) / length;
      const dy = (endY - startY) / length;
      const tailX = startX + dx * 8;
      const tailY = startY + dy * 8;
      const tipX = endX - dx * 6;
      const tipY = endY - dy * 6;
      const bend = Math.min(24, length * 0.35);
      const controlX = (tailX + tipX) / 2 - dy * bend;
      const controlY = (tailY + tipY) / 2 + dx * bend;
      // Align the arrowhead with the curve's tangent at its tip.
      const angle = Math.atan2(tipY - controlY, tipX - controlX);
      const wing = (rotation) =>
        `${tipX - 7 * Math.cos(angle + rotation)} ${tipY - 7 * Math.sin(angle + rotation)}`;
      arrowPath = `M${tailX} ${tailY}Q${controlX} ${controlY} ${tipX} ${tipY}M${wing(0.6)}L${tipX} ${tipY}L${wing(-0.6)}`;
    }

    function resolveTarget() {
      let nextElement = null;
      try {
        nextElement =
          typeof step.target === "string"
            ? document.querySelector(step.target)
            : step.target;
      } catch {
        // Invalid selectors use the same recoverable state as absent targets.
      }
      if (nextElement === element && element?.isConnected) return;
      previousGeometry = "";
      element = nextElement;
      rect = null;
      highlightRects = [];
      ready = false;
      missing = !element;
      if (element) {
        element.scrollIntoView({
          block: "nearest",
          inline: "nearest",
          behavior: "auto",
        });
        updatePosition();
      }
    }

    // Check geometry while open so scrolling, resizing and animated layout shifts stay aligned.
    function trackPosition() {
      updatePosition();
      animationFrame = requestAnimationFrame(trackPosition);
    }

    function handleTourEnter(event) {
      if (event.key !== "Enter" || event.isComposing) return;
      const guides = document.querySelectorAll(".guide-layer");
      if (guides[guides.length - 1] !== node) return;
      // Capture Enter before page controls can activate or submit a form.
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!event.repeat && !enterHeld) {
        enterHeld = true;
        index === count - 1 ? close("finish") : navigate(1);
      }
    }

    function releaseTourEnter(event) {
      if (event.key === "Enter") enterHeld = false;
    }

    function handleKeydown(event) {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        close("skip");
      }
      if (event.key !== "Tab") return;
      const selector =
        'button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';
      const candidates = [...card.querySelectorAll(selector)];
      if (interactive && element && rect) {
        candidates.unshift(...element.querySelectorAll(selector));
        if (element.matches(selector)) candidates.unshift(element);
      }
      const focusable = candidates.filter(
        (item) =>
          item.tabIndex >= 0 &&
          item.getClientRects().length &&
          getComputedStyle(item).visibility !== "hidden",
      );
      event.preventDefault();
      const active = focusable.indexOf(document.activeElement);
      const next =
        active < 0
          ? event.shiftKey
            ? focusable.length - 1
            : 0
          : (active + (event.shiftKey ? -1 : 1) + focusable.length) %
            focusable.length;
      (focusable[next] || card).focus({ preventScroll: true });
    }

    updateViewport();
    resolveTarget();
    const observer = new MutationObserver(resolveTarget);
    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener("keydown", handleTourEnter, true);
    window.addEventListener("keyup", releaseTourEnter, true);
    document.addEventListener("keydown", handleKeydown, true);
    animationFrame = requestAnimationFrame(trackPosition);
    const advanceButton = card.querySelector(
      ".guide-navigation button:last-child",
    );
    (advanceButton || card).focus({ preventScroll: true });

    return {
      destroy() {
        cancelAnimationFrame(animationFrame);
        observer.disconnect();
        window.removeEventListener("keydown", handleTourEnter, true);
        window.removeEventListener("keyup", releaseTourEnter, true);
        document.removeEventListener("keydown", handleKeydown, true);
        if (
          node.contains(document.activeElement) &&
          previousFocus?.isConnected
        ) {
          previousFocus.focus?.({ preventScroll: true });
        }
        node.remove();
      },
    };
  }
</script>

{#if open && $uiFeatureStore.platformFeatures.guidedTours}
  {#key step}
    <div class="guide-layer" use:mountGuide>
      <svg class="guide-backdrop" width="100%" height="100%" aria-hidden="true">
        <path d={backdropPath} fill-rule="evenodd" />
      </svg>
      {#if rect}
        {#each highlightRects as highlightRect}
          <div
            class="guide-highlight"
            class:blocked={!interactive}
            style={`left:${highlightRect.x}px;top:${highlightRect.y}px;width:${highlightRect.width}px;height:${highlightRect.height}px;`}
          ></div>
        {/each}
        {#if ready}
          <svg
            class="guide-arrow"
            width="100%"
            height="100%"
            aria-hidden="true"
          >
            <path d={arrowPath} />
          </svg>
        {/if}
      {/if}
      <section
        class="guide-card"
        class:centered={!ready}
        style={ready ? `left:${cardX}px;top:${cardY}px;` : ""}
        role="dialog"
        aria-label={step.title || labels.step}
        tabindex="-1"
      >
        <div class="guide-progress">
          {labels.step}
          {index + 1}
          {labels.of}
          {count}
        </div>
        <h2>{step.title}</h2>
        <div class="guide-content">
          <slot {step} {index}>{step.text || ""}</slot>
        </div>
        {#if missing}<p class="guide-missing" role="status">
            {labels.unavailable}
          </p>{/if}
        <footer>
          {#if index < count - 1}
            <Button
              kind="paxAI"
              size="field"
              text={labels.skip}
              on:click={() => close("skip")}
            />
          {/if}
          <div class="guide-navigation">
            {#if index > 0}
              <Button
                kind="outline"
                size="field"
                text={labels.previous}
                on:click={() => navigate(-1)}
              />
            {/if}
            <Button
              kind="outline"
              size="field"
              text={index === count - 1 ? labels.finish : labels.next}
              on:click={() =>
                index === count - 1 ? close("finish") : navigate(1)}
            >
              <!-- Rendered as an image so the button styles do not recolor the SVG. -->
              <img
                slot="icon"
                class="bx--btn__icon"
                src={KEYBOARD_ENTER_DARK_ICON}
                alt=""
              />
            </Button>
          </div>
        </footer>
      </section>
    </div>
  {/key}
{/if}

<style>
  .guide-layer {
    position: fixed;
    inset: 0;
    box-sizing: border-box;
    padding: 0.25rem;
    border-radius: 0.25rem;
    z-index: 50000;
    pointer-events: none;
  }
  .guide-backdrop,
  .guide-arrow {
    position: absolute;
    inset: 0;
    overflow: visible;
  }
  .guide-backdrop path {
    fill: rgba(0, 0, 0, 0.7);
    pointer-events: fill;
  }
  .guide-highlight {
    position: fixed;
    box-sizing: border-box;
    border-radius: inherit;
  }
  .guide-highlight.blocked {
    pointer-events: auto;
  }
  .guide-arrow path {
    fill: none;
    stroke: var(--pax-white);
    stroke-width: 1.75;
    stroke-linecap: round;
    stroke-linejoin: round;
    animation: guide-pulse 2.4s ease-in-out infinite;
  }
  .guide-card {
    position: fixed;
    box-sizing: border-box;
    width: min(26rem, calc(100vw - 2rem));
    max-height: calc(100dvh - 2rem);
    overflow-y: auto;
    padding: 1.5rem;
    border: 2px solid rgba(255, 255, 255, 0.5);
    border-radius: 0.5rem;
    background: var(--pax-black, #1c1c20);
    color: var(--pax-text-primary-new);
    box-shadow: 0 1rem 3rem rgba(0, 0, 0, 0.3);
    pointer-events: auto;
  }
  .guide-card.centered {
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }
  .guide-card:focus {
    outline: none;
  }
  .guide-progress {
    margin-bottom: 0.75rem;
    color: var(--pax-text-secondary-new);
    font-size: 0.5rem;
  }
  h2 {
    margin: 0 0 0.75rem;
    font-size: 1rem;
    font-weight: 700;
    line-height: 1.3;
  }
  .guide-content {
    font-size: 1rem;
    line-height: 1.6;
    white-space: pre-line;
  }
  .guide-missing {
    margin: 1rem 0 0;
    color: var(--pax-yellow-new);
    font-size: 0.875rem;
    line-height: 1.5;
  }
  footer {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
    margin-top: 1.5rem;
  }
  .guide-navigation {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-left: auto;
  }
  .guide-navigation :global(button:focus-visible) {
    outline: 2px solid var(--pax-white);
    outline-offset: 0.1875rem;
  }
  @keyframes guide-pulse {
    0%,
    100% {
      opacity: 0.95;
    }
    50% {
      opacity: 0.7;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .guide-arrow path {
      animation: none;
    }
  }
</style>
